import { BarChart3 } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAudits } from "@/hooks/use-audits";
import { useComplianceReport } from "@/hooks/use-reports";
import { AUDIT_STATUS_BADGE_VARIANTS, AUDIT_STATUS_LABELS } from "@/lib/audit-status";

function formatShortDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

export function ReportsPage() {
  const {
    data: report,
    isLoading: reportLoading,
    isError: reportError,
    refetch: refetchReport,
  } = useComplianceReport();
  const { data: audits, isLoading: auditsLoading, isError: auditsError } = useAudits();

  if (reportError) {
    return <ErrorState message="Failed to load report" onRetry={() => refetchReport()} />;
  }

  const recentAudits = (audits ?? []).slice(0, 10);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance Reports"
        description="Overview of compliance audit status and metrics."
      />

      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {reportLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cards never reorder
            <Skeleton key={i} className="h-24 w-full" />
          ))
        ) : (
          <>
            <MetricCard label="Total Audits" value={report?.total ?? 0} />
            <MetricCard
              label="Complete"
              value={report?.complete ?? 0}
              valueClass="text-green-600 dark:text-green-400"
            />
            <MetricCard label="In Progress" value={report?.inProgress ?? 0} />
            <MetricCard
              label="Blocked"
              value={report?.blocked ?? 0}
              valueClass="text-red-600 dark:text-red-400"
            />
          </>
        )}
      </div>

      {/* Recent audits table */}
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Recent Audits</h2>
        {auditsError ? (
          <ErrorState message="Failed to load audits" />
        ) : (
          <DataTable
            id="reports-recent-audits"
            headers={[
              { key: "project", label: "Project", defaultWidth: 260 },
              {
                key: "template",
                label: "Template",
                className: "hidden md:table-cell",
                defaultWidth: 280,
              },
              {
                key: "assignedTo",
                label: "Assigned To",
                className: "hidden md:table-cell",
                defaultWidth: 200,
              },
              { key: "status", label: "Status", defaultWidth: 120 },
              { key: "date", label: "Date", className: "hidden md:table-cell", defaultWidth: 110 },
            ]}
            loading={auditsLoading}
            empty={
              <EmptyState
                icon={BarChart3}
                title="No audits yet"
                description="Audits will appear here once created."
              />
            }
          >
            {recentAudits.map((audit) => (
              <tr key={audit.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium">{audit.projectName}</td>
                <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                  {audit.templateName}
                </td>
                <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                  {audit.assignedToName ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={AUDIT_STATUS_BADGE_VARIANTS[audit.status] ?? "outline"}>
                    {AUDIT_STATUS_LABELS[audit.status] ?? audit.status}
                  </Badge>
                </td>
                <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                  {formatShortDate(audit.createdAt)}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: number;
  valueClass?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-3xl font-bold ${valueClass ?? ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
